import * as React from 'react';
import moment from 'moment';
import classnames from 'classnames';
import isString from 'lodash/isString';
import kebabCase from 'lodash/kebabCase';

import StatusLabel from 'components/status-label/StatusLabel';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { getStatusLabelTextKey } from 'modules/campaigns/components/ccm/utils/ccm-campaign';

import './ccm-campaign-details-overview-campaign.scss';

applyTranslations(require('./ccm-campaign-details-overview-campaign.i18n.json'));

import type { Campaign, StatusLabelType } from 'types';

type Props = {
  campaign: Campaign,
  isCampaignCreatorless?: boolean
};

const MSG_KEY = 'ccm_campaign_details_overview_campaign';

const CcmCampaignDetailsOverviewCampaign: React.FC<Props> = ({
  campaign,
  isCampaignCreatorless
}): JSX.Element => {
  const numDaysCampaignEndedEarly = (): number => (
    moment(campaign.timeline?.campaignEndDate)
      .diff(moment(campaign.timeline?.campaignEarlyEndDate), 'days')
  );

  const getStatusLabelType = (
    textKey: string
  ): StatusLabelType => {
    if (textKey === 'ended') {
      return 'neutral';
    }

    if (textKey === 'draft' || textKey === 'outfits_in_preparation') {
      return 'warning';
    }

    return 'success';
  };

  const renderStatusLabel = (): React.ReactNode => {
    const textKey = getStatusLabelTextKey(campaign);

    return (
      <StatusLabel
        label={__t(`${MSG_KEY}.status_labels.${textKey}`)}
        type={getStatusLabelType(textKey)}
      />
    );
  };

  const renderDate = (
    type: 'start_date' | 'end_date' | 'go_live_date',
    date: string
  ): React.ReactNode => {
    const campaignEndedEarly = isString(campaign.timeline?.campaignEarlyEndDate);
    const numDaysEndedEarly = numDaysCampaignEndedEarly();
    const showAdditionalText = type === 'end_date' && campaignEndedEarly;
    const status = getStatusLabelTextKey(campaign);
    const startDateEmphasis = status === 'approved' && type === 'start_date';
    const endDateEmphasis = (status === 'outfits_in_preparation' || status === 'live' || status === 'ended')
      && type === 'end_date' && !campaignEndedEarly;

    const dateClasses = classnames('ccm-campaign-details-overview-campaign__date', {
      'ccm-campaign-details-overview-campaign__date--additional-text': showAdditionalText,
      'ccm-campaign-details-overview-campaign__date--emphasis': type === 'go_live_date',
      [`ccm-campaign-details-overview-campaign__date--${kebabCase(type)}-emphasis`]:
        (startDateEmphasis || endDateEmphasis) && !isCampaignCreatorless
    });

    return (
      <div className={dateClasses}>
        {__t(`${MSG_KEY}.${type}`)}: {date}
        {
          showAdditionalText && (
            <div>
              {__t(`${MSG_KEY}.ended_early`, {
                count: numDaysEndedEarly,
                withPlural: numDaysEndedEarly
              })}
            </div>
          )
        }
      </div>
    );
  };

  return (
    <div className="ccm-campaign-details-overview-campaign">
      <div className="ccm-campaign-details-overview-campaign__headline">
        <div
          className="ccm-campaign-details-overview-campaign__name text-overflow-ellipsis"
          title={campaign.name}
        >
          {campaign.name}
        </div>
        <div className="ccm-campaign-details-overview-campaign__dates">
          {isCampaignCreatorless ?
            renderDate('go_live_date', getDisplayDate(campaign.timeline?.taskContentGoLiveDate)) :
            renderDate('start_date', getDisplayDate(campaign.startDate))}
          {renderDate('end_date', getDisplayDate(campaign.timeline?.campaignEndDate))}
        </div>
      </div>
      {!isCampaignCreatorless && renderStatusLabel()}
    </div>
  );
};

export default CcmCampaignDetailsOverviewCampaign;
